import { FormSection, Radio } from '@ksyos/design-system';
import type { OsasEvaluationData } from '@common/validation/customControls/osas/osasEvaluationSchemas/types';
import { AssetCategory, OsasPapType } from '@common/enums';
import { useProcessActivityEditContext } from '@frontend/utils';
import {
    NarrowerTextInputWithUnitsFormGroup,
    RadioGroupFormGroup,
    TextAreaFormGroup,
} from '../../../processActivityFormGroupFields';
import { initialPressure } from '../osasEvaluationData';
import { PressureFormGroup } from './PressureFormGroup';
import { ReportFormSection } from './RapportFormSection';
import { UseFormSection } from './UseFormSection';

export function PAPFormSections() {
    const { data, onChange } = useProcessActivityEditContext<OsasEvaluationData>();

    return (
        <>
            <ReportFormSection
                data={{
                    papReportEndDate: data.papReportEndDate,
                    papReportStartDate: data.papReportStartDate,
                }}
                fieldKey={AssetCategory.papReport}
                title="PAP rapport"
            />

            <FormSection title="Apparaat en instellingen">
                <RadioGroupFormGroup
                    fieldKey="type"
                    label="Type"
                    onChange={async (value) => {
                        onChange(
                            {
                                pressureApapMaximum: initialPressure,
                                pressureApapMinimum: initialPressure,
                                pressureApapNinetyfive: initialPressure,
                                pressureCpap: initialPressure,
                                pressureEpap: initialPressure,
                                pressureIpap: initialPressure,
                                type: value as OsasPapType,
                            },
                            [
                                'type',
                                'pressureCpap',
                                'pressureIpap',
                                'pressureEpap',
                                'pressureApapMaximum',
                                'pressureApapMinimum',
                                'pressureApapNinetyfive',
                            ]
                        );
                    }}
                    radioGroupLayout="column"
                    value={data.type ?? undefined}
                >
                    <Radio
                        key={OsasPapType.cpap}
                        label={OsasPapType.cpap}
                        value={OsasPapType.cpap}
                    />

                    <Radio
                        key={OsasPapType.apap}
                        label={OsasPapType.apap}
                        value={OsasPapType.apap}
                    />

                    <Radio
                        key={OsasPapType.biPap}
                        label={OsasPapType.biPap}
                        value={OsasPapType.biPap}
                    />
                </RadioGroupFormGroup>

                {data.type === OsasPapType.cpap && (
                    <PressureFormGroup
                        fieldKey="pressureCpap"
                        label="Druk"
                    />
                )}
                {data.type === OsasPapType.apap && (
                    <>
                        <PressureFormGroup
                            fieldKey="pressureApapMinimum"
                            label="Minimale druk"
                        />
                        <PressureFormGroup
                            fieldKey="pressureApapMaximum"
                            label="Maximale druk"
                        />
                        <PressureFormGroup
                            fieldKey="pressureApapNinetyfive"
                            label="95% druk"
                        />
                    </>
                )}
                {data.type === OsasPapType.biPap && (
                    <>
                        <PressureFormGroup
                            fieldKey="pressureIpap"
                            label="Druk IPAP (Inhale)"
                        />
                        <PressureFormGroup
                            fieldKey="pressureEpap"
                            label="Druk EPAP (Exhale)"
                        />
                    </>
                )}
            </FormSection>

            <UseFormSection fieldKey="pap" />

            <FormSection title="Waarden">
                <NarrowerTextInputWithUnitsFormGroup
                    fieldKey="pAhi3Percent"
                    label="pAHI"
                    units="/ uur"
                />

                <NarrowerTextInputWithUnitsFormGroup
                    fieldKey="obstructiveApnea"
                    label="Obstructief apneu"
                    units="/ uur"
                />

                <NarrowerTextInputWithUnitsFormGroup
                    fieldKey="centralApnea"
                    label="Centraal apneu"
                    units="/ uur"
                />

                <NarrowerTextInputWithUnitsFormGroup
                    fieldKey="cheyneStokes"
                    label="Cheyne-stokes"
                />

                <TextAreaFormGroup
                    fieldKey="oxygenSaturation"
                    label="SPO2"
                />

                <TextAreaFormGroup
                    fieldKey="extraValuesReport"
                    label="Extra waarden uit rapport"
                />
            </FormSection>
        </>
    );
}
